/**
 * Agent Connection Pool
 * 
 * Manages a pool of pre-connected GenAI clients for instant agent transitions.
 * Pre-fetches tokens and establishes WebSocket connections for likely next agents
 * in the background, enabling zero-latency agent switches.
 */

import { GenAILiveClient } from './genai-live-client';
import { fetchSessionConfig, TokenContextData } from './token-service';
import { getLikelyNextAgents } from '../agents/transition-graph';
import { logger } from '../utils/logger';
import { VoiceGender } from '../agents/types';

/**
 * A pooled connection ready for instant use
 */
export interface PooledConnection {
  client: GenAILiveClient;
  agentId: string;
  model: string;
  tokenExpires: number;
  isConnected: boolean;
  isConnecting: boolean;
  context: TokenContextData;
  initialPrompt?: string;
}

/**
 * Configuration for the connection pool
 */
export interface ConnectionPoolConfig {
  backendUrl: string;
  voiceGender: VoiceGender;
  maxPoolSize?: number;
  tokenTTL?: number; // How long before token expires (ms)
  cleanupInterval?: number; // How often to clean expired connections (ms)
}

/**
 * AgentConnectionPool - Pre-warms connections for zero-latency transitions
 */
export class AgentConnectionPool {
  private pool: Map<string, PooledConnection> = new Map();
  private pendingConnections: Map<string, Promise<PooledConnection | null>> = new Map();
  private backendUrl: string;
  private voiceGender: VoiceGender;
  private maxPoolSize: number;
  private tokenTTL: number;
  private cleanupTimer: NodeJS.Timeout | null = null;
  private isDestroyed: boolean = false;

  constructor(config: ConnectionPoolConfig) {
    this.backendUrl = config.backendUrl;
    this.voiceGender = config.voiceGender;
    this.maxPoolSize = config.maxPoolSize || 5;
    this.tokenTTL = config.tokenTTL || 45000; // 45 seconds (tokens valid for 1 min)
    
    // Start cleanup timer
    const cleanupInterval = config.cleanupInterval || 30000; // 30 seconds
    this.cleanupTimer = setInterval(() => this.cleanup(), cleanupInterval);
    
    logger.info('[ConnectionPool] Initialized with maxSize:', this.maxPoolSize, 'TTL:', this.tokenTTL);
  }

  /**
   * Update pool configuration (e.g., when voice gender changes)
   */
  public updateConfig(config: Partial<ConnectionPoolConfig>): void {
    if (config.backendUrl) this.backendUrl = config.backendUrl;
    if (config.voiceGender) this.voiceGender = config.voiceGender;
    if (config.maxPoolSize) this.maxPoolSize = config.maxPoolSize;
    if (config.tokenTTL) this.tokenTTL = config.tokenTTL;
    
    // Clear pool when config changes as connections may be stale
    this.clearPool();
  }

  /**
   * Pre-warm connections for likely next agents based on current agent
   * This is called after an agent is activated
   */
  public async warmPool(currentAgentId: string, context: TokenContextData): Promise<void> {
    if (this.isDestroyed) return;
    
    const likelyNextAgents = getLikelyNextAgents(currentAgentId);
    
    if (likelyNextAgents.length === 0) {
      logger.info('[ConnectionPool] No likely next agents for:', currentAgentId);
      return;
    }
    
    logger.info('[ConnectionPool] Warming pool for likely next agents:', likelyNextAgents);
    
    // Pre-connect each likely agent in parallel (but don't await - fire and forget)
    for (const agentId of likelyNextAgents) {
      // Skip if already in pool and valid
      const existing = this.pool.get(agentId);
      if (existing && existing.isConnected && !this.isExpired(existing)) {
        logger.info('[ConnectionPool] Agent already warm:', agentId);
        continue;
      }
      
      // Skip if connection is already in progress
      if (this.pendingConnections.has(agentId)) {
        logger.info('[ConnectionPool] Agent connection already pending:', agentId);
        continue;
      }
      
      // Check pool size limit
      if (this.pool.size >= this.maxPoolSize) {
        logger.warn('[ConnectionPool] Pool full, skipping:', agentId);
        continue;
      }
      
      // Start pre-connection in background
      this.preconnectAgent(agentId, context).catch(error => {
        logger.warn('[ConnectionPool] Pre-connect failed for:', agentId, error);
      });
    }
  }

  /**
   * Pre-connect a specific agent in the background
   */
  private async preconnectAgent(agentId: string, context: TokenContextData): Promise<PooledConnection | null> {
    if (this.isDestroyed) return null;
    
    // Check if already pending
    const pending = this.pendingConnections.get(agentId);
    if (pending) {
      return pending;
    }
    
    logger.info('[ConnectionPool] Pre-connecting agent:', agentId);
    
    const connectionPromise = this.createConnection(agentId, context);
    this.pendingConnections.set(agentId, connectionPromise);
    
    try {
      const connection = await connectionPromise;
      this.pendingConnections.delete(agentId);
      
      if (connection) {
        this.pool.set(agentId, connection);
        logger.info('[ConnectionPool] Agent pre-connected and added to pool:', agentId);
      }
      
      return connection;
    } catch (error) {
      this.pendingConnections.delete(agentId);
      throw error;
    }
  }

  /**
   * Create a new connection for an agent
   */
  private async createConnection(agentId: string, context: TokenContextData): Promise<PooledConnection | null> {
    if (this.isDestroyed) return null;
    
    try {
      // Fetch session config from backend
      const sessionConfig = await fetchSessionConfig(
        this.backendUrl,
        agentId,
        this.voiceGender,
        context
      );
      
      if (this.isDestroyed) return null;
      
      // Create client with Azure OpenAI config (no API key for proxy mode)
      const client = new GenAILiveClient({
        endpoint: sessionConfig.endpoint,
        deployment: sessionConfig.deployment,
        apiVersion: sessionConfig.api_version,
      });
      
      // Create connection object
      const connection: PooledConnection = {
        client,
        agentId,
        model: sessionConfig.deployment,
        tokenExpires: Date.now() + this.tokenTTL,
        isConnected: false,
        isConnecting: true,
        context,
        initialPrompt: sessionConfig.initial_prompt,
      };
      
      // Connect via backend proxy (API key stays server-side)
      const connected = await client.connectViaProxy(this.backendUrl, agentId, this.voiceGender);
      
      if (this.isDestroyed) {
        client.disconnect();
        return null;
      }
      
      connection.isConnected = connected;
      connection.isConnecting = false;
      
      if (connected) {
        logger.info('[ConnectionPool] WebSocket connected for:', agentId);
      } else {
        logger.warn('[ConnectionPool] WebSocket connection failed for:', agentId);
        return null;
      }
      
      return connection;
      
    } catch (error) {
      logger.error('[ConnectionPool] Failed to create connection for:', agentId, error);
      return null;
    }
  }

  /**
   * Get a pre-warmed connection from the pool
   * Returns null if no valid connection is available
   */
  public getWarmConnection(agentId: string): PooledConnection | null {
    const connection = this.pool.get(agentId);
    
    if (!connection) {
      logger.info('[ConnectionPool] No warm connection for:', agentId);
      return null;
    }
    
    // Check if expired
    if (this.isExpired(connection)) {
      logger.info('[ConnectionPool] Connection expired for:', agentId);
      this.removeConnection(agentId);
      return null;
    }
    
    // Check if connected
    if (!connection.isConnected) {
      logger.info('[ConnectionPool] Connection not ready for:', agentId);
      return null;
    }
    
    // Remove from pool (single use)
    this.pool.delete(agentId);
    
    logger.info('[ConnectionPool] Providing warm connection for:', agentId);
    return connection;
  }

  /**
   * Check if a warm connection is available for an agent
   */
  public hasWarmConnection(agentId: string): boolean {
    const connection = this.pool.get(agentId);
    return connection !== null && 
           connection !== undefined && 
           connection.isConnected && 
           !this.isExpired(connection);
  }

  /**
   * Wait for a pending connection to complete
   */
  public async waitForConnection(agentId: string): Promise<PooledConnection | null> {
    const pending = this.pendingConnections.get(agentId);
    if (pending) {
      logger.info('[ConnectionPool] Waiting for pending connection:', agentId);
      return pending;
    }
    return this.getWarmConnection(agentId);
  }

  /**
   * Check if a connection is expired
   */
  private isExpired(connection: PooledConnection): boolean {
    return Date.now() > connection.tokenExpires;
  }

  /**
   * Remove a connection from the pool
   */
  private removeConnection(agentId: string): void {
    const connection = this.pool.get(agentId);
    if (connection) {
      connection.client.disconnect();
      this.pool.delete(agentId);
      logger.info('[ConnectionPool] Removed connection:', agentId);
    }
  }

  /**
   * Clean up expired connections
   */
  public cleanup(): void {
    if (this.isDestroyed) return;
    
    const now = Date.now();
    const expiredAgents: string[] = [];
    
    this.pool.forEach((connection, agentId) => {
      if (now > connection.tokenExpires) {
        expiredAgents.push(agentId);
      }
    });
    
    expiredAgents.forEach(agentId => {
      this.removeConnection(agentId);
    });
    
    if (expiredAgents.length > 0) {
      logger.info('[ConnectionPool] Cleaned up expired connections:', expiredAgents);
    }
  }

  /**
   * Clear all connections from the pool
   */
  public clearPool(): void {
    logger.info('[ConnectionPool] Clearing all connections');
    
    this.pool.forEach((connection) => {
      connection.client.disconnect();
    });
    
    this.pool.clear();
    this.pendingConnections.clear();
  }

  /**
   * Get current pool status for debugging
   */
  public getStatus(): { poolSize: number; agents: string[]; pendingCount: number } {
    return {
      poolSize: this.pool.size,
      agents: Array.from(this.pool.keys()),
      pendingCount: this.pendingConnections.size,
    };
  }

  /**
   * Destroy the pool and clean up all resources
   */
  public destroy(): void {
    logger.info('[ConnectionPool] Destroying pool');
    
    this.isDestroyed = true;
    
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    
    this.clearPool();
  }
}

/**
 * Create a new AgentConnectionPool instance
 */
export function createConnectionPool(config: ConnectionPoolConfig): AgentConnectionPool {
  return new AgentConnectionPool(config);
}

